"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import SmbPlansDropdown from "./SmbPlansDropdown";
import { useCurrency } from "@/lib/useCurrency";
import {
  contactHref,
  contactLabel,
  formatMoney,
  formatRate,
  getPlanPricing,
  resolveCheckoutUrl,
  YEARLY_DISCOUNT_PCT,
  type BillingPeriod,
  type PricingPlanKey,
} from "@/lib/constants/pricing-display";

interface MainPlan {
  pricingKey: PricingPlanKey;
  title: string;
  tagline: string;
  bestFor: string;
  description: string;
  /** Static feature lines; the overage line is injected dynamically. */
  features: string[];
  featured: boolean;
  cta: string;
  stripeNote: string;
}

const plans: MainPlan[] = [
  {
    pricingKey: "business",
    title: "Business Plan",
    tagline: "Scale your capabilities",
    bestFor: "Best for growing teams",
    description:
      "Scale your capabilities with a plan built for growing teams that want multilingual voice outreach, predictable monthly access, and a more self-directed operating model.",
    features: [
      "Includes 100 minutes per month",
      "Designed for growing teams and scaling",
      "Communicate in 10+ languages",
      "Up to 10 voice AI agents",
      "Standard reports with CSV exports",
      "Limited analytics",
      "Priority email support (24-hour response)",
      "Shared responsibility model. Tools provided, client manages inputs",
      "Lower minute pricing unlocked through subscription",
      "Reduces manual outreach and improves engagement",
    ],
    featured: false,
    cta: "Start Business Plan",
    stripeNote: "Stripe Checkout",
  },
  {
    pricingKey: "enterprise",
    title: "Enterprise Plan",
    tagline: "Do more with enterprise",
    bestFor: "Best for complex or high-volume projects",
    description:
      "Support larger-scale voice operations with lower overage pricing, more included minutes, advanced reporting, and dedicated hands-on project oversight.",
    features: [
      "Includes 250 minutes per month",
      "Full-scale solution for larger operations",
      "Communicate in 55+ languages",
      "Up to 20 voice AI agents",
      "Advanced analytics, multilingual support, and compliance features",
      "Dedicated project manager with end-to-end oversight. Priced per engagement.",
      "Results delivered through your reporting workflow",
      "Custom campaign design and optimization included",
      "Premium support with direct line access",
      "End-to-end project management. No staffing headaches",
      "Optimized for higher-volume projects with lower minute pricing",
      "AI Campaign Intelligence Reports (included)",
    ],
    featured: true,
    cta: "Start Enterprise Plan",
    stripeNote: "Stripe Checkout",
  },
];

function BillingToggle({
  billingPeriod,
  onChange,
}: {
  billingPeriod: BillingPeriod;
  onChange: (period: BillingPeriod) => void;
}) {
  const options: { value: BillingPeriod; label: string }[] = [
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: `Yearly (Save ${YEARLY_DISCOUNT_PCT}%)` },
  ];
  return (
    <div
      role="group"
      aria-label="Billing period"
      style={{
        display: "flex",
        justifyContent: "center",
        gap: 4,
        margin: "0 auto 36px",
        padding: 4,
        width: "fit-content",
        background: "var(--color-bg)",
        border: "1px solid var(--color-border)",
        borderRadius: 999,
      }}
    >
      {options.map((opt) => {
        const active = billingPeriod === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            style={{
              padding: "10px 22px",
              borderRadius: 999,
              border: "none",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontSize: "0.85rem",
              fontWeight: 600,
              background: active ? "var(--color-text)" : "transparent",
              color: active ? "#fff" : "var(--color-text-muted)",
              transition: "all 0.2s",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default function Pricing() {
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
  // Currency is now chosen via the header flag switch and the first-visit
  // welcome modal; this component only reads the current value (both write
  // through the same useCurrency hook, so prices react to those changes).
  const { currency } = useCurrency();
  const yearly = billingPeriod === "yearly";

  return (
    <section id="pricing" className="section-padding" style={{ background: "var(--color-bg-alt)" }}>
      <div className="section-inner" style={{ maxWidth: 1000 }}>
        <h2 className="section-heading">
          Choose the plan that{" "}
          <em style={{ fontFamily: "var(--font-heading)" }}>fits your research firm or call center</em>
        </h2>
        <p className="section-subheading">
          Clear pricing. No hidden fees. No setup fee on Small Business plans.
          Pay yearly and save {YEARLY_DISCOUNT_PCT}%. All prices in {currency}.
        </p>

        {/* Monthly / Yearly toggle (controls the SMB cards and the main cards).
            Currency selection now lives in the header flag switch + first-visit
            welcome modal. */}
        <BillingToggle billingPeriod={billingPeriod} onChange={setBillingPeriod} />

        {/* Small Business plans, shown above the Business / Enterprise cards */}
        <SmbPlansDropdown billingPeriod={billingPeriod} currency={currency} />

        {/* Plan cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
            gap: 28,
            marginBottom: 40,
          }}
        >
          {plans.map((plan, i) => {
            const pricing = getPlanPricing(currency, plan.pricingKey);
            // Resolve the live checkout link for the current currency+interval;
            // null -> contact fallback. USD links are env-driven (currently
            // absent) so we never point a USD price at a CAD checkout.
            const url = resolveCheckoutUrl(currency, plan.pricingKey, billingPeriod);
            const href = url ?? contactHref(currency);
            const isExternal = href.startsWith("http");
            const ctaLabel = url ? plan.cta : contactLabel(currency);
            const displayedPerMonth = yearly ? pricing.yearlyPerMonth : pricing.price;

            const features = [
              plan.features[0],
              `${formatRate(pricing.overage)} ${currency} per additional minute`,
              ...plan.features.slice(1),
            ];

            return (
              <motion.div
                key={plan.title}
                className={`pricing-card${plan.featured ? " featured" : ""}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
              >
                {/* Best for badge */}
                <span
                  style={{
                    display: "inline-block",
                    fontFamily: "var(--font-body)",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: plan.featured ? "var(--color-accent)" : "var(--color-text-muted)",
                    marginBottom: 12,
                  }}
                >
                  {plan.bestFor}
                </span>

                <h3
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "1.5rem",
                    color: "var(--color-text)",
                    marginBottom: 4,
                  }}
                >
                  {plan.title}
                </h3>
                <p
                  style={{
                    fontSize: "0.88rem",
                    color: "var(--color-text-muted)",
                    fontWeight: 300,
                    marginBottom: 20,
                  }}
                >
                  {plan.tagline}
                </p>

                {/* Price */}
                <div style={{ marginBottom: 6 }}>
                  <span
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "2.8rem",
                      color: "var(--color-text)",
                      lineHeight: 1,
                    }}
                  >
                    {formatMoney(displayedPerMonth, currency)}
                  </span>
                  <span
                    style={{
                      fontSize: "0.88rem",
                      color: "var(--color-text-muted)",
                      fontWeight: 300,
                      marginLeft: 6,
                    }}
                  >
                    {currency} / month
                  </span>
                </div>
                <p
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--color-text-muted)",
                    fontWeight: 300,
                    marginBottom: 20,
                  }}
                >
                  {yearly
                    ? `${formatMoney(pricing.yearlyTotal, currency)} ${currency} billed annually (save ${YEARLY_DISCOUNT_PCT}%) + one-time ${formatMoney(pricing.setupFee, currency)} setup fee`
                    : `Billed monthly + one-time ${formatMoney(pricing.setupFee, currency)} setup fee`}
                </p>

                <p
                  style={{
                    fontSize: "0.88rem",
                    color: "var(--color-text-muted)",
                    fontWeight: 300,
                    lineHeight: 1.7,
                    marginBottom: 28,
                    paddingBottom: 24,
                    borderBottom: "1px solid var(--color-border)",
                  }}
                >
                  {plan.description}
                </p>

                {/* Features */}
                <ul style={{ listStyle: "none", padding: 0, marginBottom: 32 }}>
                  {features.map((f) => (
                    <li
                      key={f}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        marginBottom: 12,
                        fontSize: "0.88rem",
                        color: "var(--color-text)",
                        fontWeight: 400,
                        lineHeight: 1.5,
                      }}
                    >
                      <Check
                        size={16}
                        style={{ flexShrink: 0, marginTop: 3 }}
                        color={plan.featured ? "var(--color-accent)" : "var(--color-text-muted)"}
                        strokeWidth={2}
                      />
                      {f}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <a
                  href={href}
                  {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="btn-primary"
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    background: plan.featured ? "var(--color-text)" : "transparent",
                    color: plan.featured ? "#fff" : "var(--color-text)",
                    border: plan.featured ? "none" : "1.5px solid var(--color-border)",
                    padding: "16px 24px",
                  }}
                >
                  {ctaLabel}
                  <ArrowRight size={16} />
                </a>
                <p
                  style={{
                    textAlign: "center",
                    marginTop: 10,
                    fontSize: "0.75rem",
                    color: "var(--color-text-muted)",
                    fontWeight: 300,
                  }}
                >
                  Powered by {plan.stripeNote}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Comparison row */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16,
            maxWidth: 700,
            margin: "0 auto 32px",
          }}
        >
          {[
            {
              plan: "Business Plan",
              details: `100 included minutes · ${formatRate(getPlanPricing(currency, "business").overage)} ${currency}/min overage`,
            },
            {
              plan: "Enterprise Plan",
              details: `250 included minutes · ${formatRate(getPlanPricing(currency, "enterprise").overage)} ${currency}/min overage`,
            },
          ].map((p) => (
            <div
              key={p.plan}
              style={{
                background: "var(--color-bg)",
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: "16px",
                textAlign: "center",
              }}
            >
              <p style={{ fontWeight: 600, fontSize: "0.82rem", marginBottom: 4 }}>{p.plan}</p>
              <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", fontWeight: 300 }}>
                {p.details}
              </p>
            </div>
          ))}
        </motion.div>

        {/* Pricing note */}
        <p
          style={{
            textAlign: "center",
            fontSize: "0.85rem",
            color: "var(--color-text-muted)",
            fontWeight: 300,
            lineHeight: 1.7,
            maxWidth: 680,
            margin: "0 auto",
          }}
        >
          As your usage grows, your effective cost per minute drops. Higher plans
          are designed to support bigger projects with stronger economics and
          deeper support. Small Business plans have no setup fee; Business and
          Enterprise carry a one-time {formatMoney(getPlanPricing(currency, "business").setupFee, currency)} setup fee.
        </p>
      </div>
    </section>
  );
}
